import { useState } from 'react';
import styles from '../Account.module.css';
import AccountCard from '../AccountCard';
import SectionHead from '../SectionHead';
import StatusBadge from '../StatusBadge';
import Icon from '../Icon';
import { supabase } from '../../../lib/supabaseClient';

// Best-effort label for the current browser/OS - used to describe *this* session.
// No fake device list is invented; we only show the session we can actually see.
function currentDevice() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Linux/.test(ua) ? 'Linux' : 'Unknown OS';
  return `${browser} · ${os}`;
}

export default function SecuritySection({ onLogoutAll }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState({ next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type:'ok'|'err', text }

  async function changePassword(e) {
    e.preventDefault();
    setMsg(null);
    if (pw.next.length < 8) return setMsg({ type: 'err', text: 'Password must be at least 8 characters.' });
    if (pw.next !== pw.confirm) return setMsg({ type: 'err', text: 'Passwords do not match.' });
    if (!supabase) return setMsg({ type: 'err', text: 'Auth is not configured in this environment.' });

    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw.next });
    setBusy(false);
    if (error) return setMsg({ type: 'err', text: error.message });
    setMsg({ type: 'ok', text: 'Password updated.' });
    setPw({ next: '', confirm: '' });
    setOpen(false);
  }

  return (
    <>
      <SectionHead title="Security" subtitle="Keep your account safe. Manage your password, sessions and account." />

      {/* ── Change password ── */}
      <AccountCard
        title="Password"
        description="Set a strong, unique password - at least 8 characters."
        actions={!open && (
          <button className={`${styles.btnGhost} ${styles.btnSmall}`} onClick={() => setOpen(true)}>Change password</button>
        )}
      >
        {!open ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className={styles.quickIcon} aria-hidden="true"><Icon name="lock" size={18} /></span>
            <p className={styles.note} style={{ margin: 0 }}>
              Your password was set when you registered. Change it any time.
            </p>
          </div>
        ) : (
          <form onSubmit={changePassword}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>New password</label>
              <input type="password" className={styles.input} value={pw.next}
                onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Confirm new password</label>
              <input type="password" className={styles.input} value={pw.confirm}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.btnGhost} onClick={() => { setOpen(false); setMsg(null); }} disabled={busy}>Cancel</button>
              <button type="submit" className={styles.btnPrimary} disabled={busy}>{busy ? 'Saving…' : 'Update password'}</button>
            </div>
          </form>
        )}
        {msg && <p className={styles.note} style={{ color: msg.type === 'ok' ? '#22c55e' : '#ef4444' }}>{msg.text}</p>}
      </AccountCard>

      {/* ── Two-factor authentication ── */}
      <AccountCard title="Two-factor authentication" description="Add a second step at login for extra protection.">
        <div className={styles.secRow} style={{ paddingTop: 0, borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span className={styles.quickIcon} aria-hidden="true"><Icon name="shield" size={18} /></span>
            <div className={styles.secInfo}>
              <div className={styles.secTitle}>Authenticator app (2FA)</div>
              <div className={styles.secDesc}>Protect your account with a time-based one-time code.</div>
            </div>
          </div>
          <button className={`${styles.btnGhost} ${styles.btnSmall}`} disabled title="Coming soon">Coming soon</button>
        </div>
      </AccountCard>

      {/* ── Active sessions ── */}
      <AccountCard
        title="Active sessions"
        description="Devices currently signed in to your account."
        actions={<button className={`${styles.btnDanger} ${styles.btnSmall}`} onClick={onLogoutAll}>Log out everywhere</button>}
      >
        <div className={styles.secRow} style={{ paddingTop: 0, borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span className={styles.deviceIcon} aria-hidden="true"><Icon name="monitor" size={16} /></span>
            <div className={styles.secInfo}>
              <div className={styles.secTitle}>This device</div>
              <div className={styles.secDesc}>{currentDevice()} · active now</div>
            </div>
          </div>
          <StatusBadge status="active" label="Current" />
        </div>
        <div className={styles.trustLine}>
          <span className={styles.trustIcon} aria-hidden="true"><Icon name="clock" size={13} /></span>
          A detailed list of all sessions across devices is coming soon.
        </div>
      </AccountCard>

      {/* ── Danger zone ── */}
      <AccountCard title="Danger zone" description="Irreversible actions for your account." className={styles.dangerCard}>
        <div className={styles.secRow} style={{ paddingTop: 0, borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span className={styles.quickIcon} aria-hidden="true" style={{ color: '#ef8080' }}><Icon name="trash" size={17} /></span>
            <div className={styles.secInfo}>
              <div className={styles.secTitle}>Delete account</div>
              <div className={styles.secDesc}>Permanently delete your account and all associated data.</div>
            </div>
          </div>
          <button className={`${styles.btnGhost} ${styles.btnSmall}`} disabled title="Coming soon">Coming soon</button>
        </div>
      </AccountCard>
    </>
  );
}
